import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  getWhatsAppLinkStatus,
  getWhatsAppSendStatus,
  resetWhatsAppBridgeSession,
  sendWhatsAppAdmin,
} from '../lib/api';
import { toast } from 'sonner';
import { MessageCircle, RefreshCw, Send, Smartphone, Unplug } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const POLL_MS = 2500;
const SEND_STATUS_POLL_MS = 2000;
const SEND_STATUS_TIMEOUT_MS = 60_000;

export default function AdminWhatsApp() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [sendPhone, setSendPhone] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sending, setSending] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await getWhatsAppLinkStatus();
      setStatus(data);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        'No se pudo obtener el estado del puente';
      setStatus({ error: typeof msg === 'string' ? msg : JSON.stringify(msg) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!dialogOpen) return undefined;
    const id = setInterval(() => {
      fetchStatus();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [dialogOpen, fetchStatus]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setLoading(true);
    fetchStatus();
  };

  const trackSendDelivery = useCallback(async (requestId, phoneRaw) => {
    const startedAt = Date.now();
    let alreadyNotifiedSent = false;
    while (Date.now() - startedAt <= SEND_STATUS_TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, SEND_STATUS_POLL_MS));
      try {
        const st = await getWhatsAppSendStatus(requestId);
        const state = String(st?.status || '').toLowerCase();
        if (state === 'failed') {
          const detail = st?.error ? ` (${st.error})` : '';
          toast.error(`WhatsApp reportó fallo al enviar${detail}`);
          return;
        }
        if (state === 'sent' && !alreadyNotifiedSent) {
          alreadyNotifiedSent = true;
          toast.info('WhatsApp aceptó el envío. Esperando confirmación de entrega…');
        }
        if (state === 'delivered') {
          toast.success(`Mensaje entregado a ${phoneRaw}.`);
          return;
        }
        if (state === 'read') {
          toast.success(`Mensaje leído por ${phoneRaw}.`);
          return;
        }
      } catch (e) {
        const code = e?.response?.status;
        if (code === 404) {
          toast.info('El seguimiento del envío expiró; revisa en el teléfono si se recibió.');
          return;
        }
      }
    }
    toast.info('El mensaje sigue en proceso. Revisa WhatsApp para confirmar entrega final.');
  }, []);

  const handleSend = useCallback(async (e) => {
    e.preventDefault();
    if (!sendPhone.trim() || !sendMessage.trim()) {
      toast.error('Indica número y mensaje.');
      return;
    }
    setSending(true);
    try {
      const phone = sendPhone.trim();
      const out = await sendWhatsAppAdmin(phone, sendMessage.trim());
      if (out?.ok) {
        toast.success('Mensaje aceptado por el puente (encolado). Entrega al teléfono pendiente de confirmación de WhatsApp.');
        if (out?.requestId) {
          void trackSendDelivery(out.requestId, phone);
        }
        setSendMessage('');
      } else {
        toast.error(out?.error || 'No se pudo enviar');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Error al enviar';
      toast.error(typeof msg === 'string' ? msg : 'Error al enviar');
    } finally {
      setSending(false);
    }
  }, [trackSendDelivery, sendMessage, sendPhone]);

  const handleReset = async () => {
    if (!window.confirm('¿Desvincular WhatsApp en el servidor y generar un código QR nuevo?')) return;
    setResetting(true);
    try {
      await resetWhatsAppBridgeSession();
      toast.success('Sesión reiniciada. Escanea el nuevo código cuando aparezca.');
      await fetchStatus();
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        'Error al reiniciar';
      toast.error(typeof msg === 'string' ? msg : 'Error al reiniciar');
    } finally {
      setResetting(false);
    }
  };

  const linked = Boolean(status?.linked);
  const qr = status?.qrDataUrl;
  const phone = status?.phone;
  const connection = status?.connection;
  const bridgeError = status?.error;
  const lastCode = status?.lastDisconnectCode;
  const lastMsg = status?.lastDisconnectMessage;
  const reconnectPaused = Boolean(status?.reconnectPaused);
  const is405 = Number(lastCode) === 405 || /405/.test(String(lastMsg || ''));

  return (
    <div data-testid="admin-whatsapp">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground">WhatsApp</h1>
        <p className="text-muted-foreground">
          Vincula el número de la clínica con el puente Baileys y envía mensajes manuales a clientes.
        </p>
      </div>

      <Card className="mb-8 max-w-xl border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Send className="w-5 h-5 text-primary" />
            Enviar mensaje
          </CardTitle>
          <CardDescription>
            Solo administradores. Requiere WhatsApp vinculado. México: 10 dígitos (LADA+número) o completo con
            521… (móvil); si pegas 52 + 10 dígitos y no llega, prueba con 521 delante.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSend} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="wa-send-phone">Teléfono del destinatario</Label>
              <Input
                id="wa-send-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="Ej. 8112141456 o 5218112141456"
                value={sendPhone}
                onChange={(ev) => setSendPhone(ev.target.value)}
                disabled={sending || Boolean(bridgeError)}
                data-testid="wa-send-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wa-send-text">Mensaje</Label>
              <Textarea
                id="wa-send-text"
                rows={4}
                placeholder="Escribe el mensaje…"
                value={sendMessage}
                onChange={(ev) => setSendMessage(ev.target.value)}
                disabled={sending || Boolean(bridgeError)}
                data-testid="wa-send-text"
              />
            </div>
            <Button
              type="submit"
              disabled={sending || Boolean(bridgeError) || !linked}
              title={!linked ? 'Primero vincula WhatsApp (QR)' : undefined}
              data-testid="wa-send-submit"
            >
              <Send className="mr-2 h-4 w-4" />
              {sending ? 'Enviando…' : 'Enviar por WhatsApp'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Nota: este envío confirma aceptación en el puente (cola), no la entrega final al destinatario.
            </p>
            {!linked && !bridgeError && (
              <p className="text-xs text-muted-foreground">
                El botón se activa cuando el estado muestre &quot;Vinculado&quot; (sesión abierta en el puente).
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      <Card className="max-w-lg border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageCircle className="w-5 h-5 text-primary" />
            Emparejamiento
          </CardTitle>
          <CardDescription>
            Si el servidor aún no tiene sesión, verás el código QR para escanearlo desde WhatsApp →
            Dispositivos vinculados.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && !status ? (
            <p className="text-sm text-muted-foreground">Cargando estado…</p>
          ) : bridgeError ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {bridgeError}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {linked ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-success/15 px-3 py-1 font-medium text-success">
                  <Smartphone className="w-4 h-4" />
                  Vinculado{phone ? ` · ${phone}` : ''}
                </span>
              ) : qr ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-warning/15 px-3 py-1 font-medium text-warning">
                  Esperando escaneo del QR
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 font-medium text-muted-foreground">
                  Sin sesión · {connection || '—'}
                </span>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={handleOpenDialog} data-testid="whatsapp-open-dialog">
              {linked ? 'Ver estado / QR' : 'Mostrar código QR'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setLoading(true);
                fetchStatus();
              }}
              disabled={loading}
              data-testid="whatsapp-refresh"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleReset}
              disabled={resetting || Boolean(bridgeError)}
              data-testid="whatsapp-reset"
            >
              <Unplug className="mr-2 h-4 w-4" />
              {resetting ? 'Reiniciando…' : 'Desvincular y nuevo QR'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="whatsapp-dialog">
          <DialogHeader>
            <DialogTitle>Vincular WhatsApp</DialogTitle>
            <DialogDescription>
              En el móvil: Ajustes → Dispositivos vinculados → Vincular dispositivo. Escanea el código
              cuando aparezca.
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 py-2">
            {bridgeError ? (
              <p className="text-center text-sm text-destructive">{bridgeError}</p>
            ) : linked ? (
              <div className="text-center text-sm text-muted-foreground">
                <p className="font-medium text-foreground">Ya hay una sesión activa.</p>
                {phone && <p className="mt-1">{phone}</p>}
                <p className="mt-2">No hace falta escanear un QR mientras siga vinculado.</p>
              </div>
            ) : qr ? (
              <img src={qr} alt="Código QR de WhatsApp" className="h-64 w-64 rounded-lg border bg-white p-2" />
            ) : (
              <div className="space-y-3 text-center text-sm text-muted-foreground">
                <p>
                  <span className="font-medium text-foreground">Aún no hay código QR en pantalla.</span>
                  {connection ? (
                    <span className="mt-2 block">
                      Estado del puente Baileys: <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{connection}</code>
                    </span>
                  ) : null}
                </p>
                {is405 || reconnectPaused ? (
                  <p className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-left text-xs text-foreground">
                    <strong>WhatsApp rechazó la conexión (405).</strong> No es un fallo del panel: la IP del servidor
                    no puede abrir el WebSocket hacia Meta. <strong>No pulses Desvincular en bucle</strong> (empeora el
                    bloqueo). Espera 15–60 minutos (o rota la IP del droplet) y luego una sola vez{' '}
                    <strong>Desvincular y nuevo QR</strong>.
                    {lastMsg ? (
                      <span className="mt-1 block text-muted-foreground">{lastMsg}</span>
                    ) : null}
                  </p>
                ) : String(connection || '').toLowerCase() === 'close' ? (
                  <p className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2 text-left text-xs text-foreground">
                    <strong>Sesión cerrada (close).</strong> No es un fallo del panel: el cliente WhatsApp no está conectado.
                    Cierra este cuadro y pulsa <strong>Desvincular y nuevo QR</strong> (borra la sesión en el servidor y pide un código
                    nuevo). Abre de nuevo «Mostrar código QR» y espera 15–40 s; si no aparece, revisa los logs:{' '}
                    <code className="rounded bg-muted px-1 text-[11px]">docker logs wa-node-bridge --tail 80</code>
                    {' '}(busca 405, 440 o errores de red).
                  </p>
                ) : null}
                <ol className="list-decimal space-y-2 pl-5 text-left text-xs">
                  <li>
                    Espera 10–30 s y pulsa <strong>Actualizar</strong> en la pantalla anterior; el QR aparece cuando WhatsApp lo envía al
                    servidor.
                  </li>
                  <li>
                    Si el estado sigue en <code className="rounded bg-muted px-1">close</code> sin QR, usa <strong>Desvincular y nuevo QR</strong>{' '}
                    (no solo Actualizar).
                  </li>
                  <li>
                    Si arriba aparece un <span className="text-destructive">error en rojo</span>, el backend no está llegando al puente:
                    revisa que el contenedor <code className="rounded bg-muted px-1">node-bridge</code> esté en marcha y que{' '}
                    <code className="rounded bg-muted px-1">WA_BRIDGE_URL</code> / <code className="rounded bg-muted px-1">WA_BRIDGE_TOKEN</code>{' '}
                    en el backend coincidan con <code className="rounded bg-muted px-1">WA_BRIDGE_STATUS_TOKEN</code> del puente.
                  </li>
                </ol>
                <details className="text-left text-xs">
                  <summary className="cursor-pointer text-muted-foreground underline-offset-2 hover:underline">
                    Comando en servidor (EC2) si hace falta levantar el puente
                  </summary>
                  <pre className="mt-2 max-h-32 overflow-auto rounded-md border bg-muted/50 p-2 font-mono text-[11px] leading-relaxed">
                    {`cd ~/whatsapp-qr-bridge && docker compose -f docker-compose.ec2.yml up -d\ncurl -sS -H "X-Bridge-Token: <token>" http://127.0.0.1:8787/status`}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
